import { FC } from 'react';
import { IPaymentDispute } from 'interfaces/stripe';
import { datetimeFormat, DATE_TIME_FORMAT } from 'utils/datetime';
import { convertSnakeCaseToReadable } from 'utils/common';

interface DisputeDetailProps {
  dispute: IPaymentDispute;
  timezone: string;
}

const DISPUTE_STATUS_LABEL: Record<string, string> = {
  warning_needs_response: 'Inquiry — needs response',
  warning_under_review: 'Inquiry — under review',
  warning_closed: 'Inquiry closed',
  needs_response: 'Needs response',
  under_review: 'Under review',
  won: 'Won',
  lost: 'Lost',
  prevented: 'Prevented',
};

const DisputeDetail: FC<DisputeDetailProps> = ({ dispute, timezone }) => {
  const { status, evidenceDueBy } = dispute;

  const statusLabel = status
    ? (DISPUTE_STATUS_LABEL[status] ?? convertSnakeCaseToReadable(status))
    : 'Unknown';

  return (
    <div className="mt-0.4">
      <div className="text-14 text-darkest-grey">
        Dispute {statusLabel.toLowerCase()}
      </div>
      {evidenceDueBy && (
        <div className="text-12 text-[#9CA3AF]">
          Respond by{' '}
          {datetimeFormat({
            dateString: evidenceDueBy,
            pattern: DATE_TIME_FORMAT.MMM_DD_YYYY,
            timezone,
          })}
        </div>
      )}
    </div>
  );
};

const DisputeDetailTooltip: FC<DisputeDetailProps> = ({
  dispute,
  timezone,
}) => {
  const { status, reason, evidenceDueBy } = dispute;

  const statusLabel = status
    ? (DISPUTE_STATUS_LABEL[status] ?? convertSnakeCaseToReadable(status))
    : 'Unknown';

  return (
    <div className="flex flex-col gap-y-0.4 text-11 font-normal normal-case leading-[1.6rem]">
      <div className="flex gap-x-1">
        <span className="w-7 shrink-0 text-mid-grey">Status</span>
        <span className="text-darkest-grey">{statusLabel}</span>
      </div>
      <div className="flex gap-x-1">
        <span className="w-7 shrink-0 text-mid-grey">Reason</span>
        <span className="text-darkest-grey">
          {reason ? convertSnakeCaseToReadable(reason) : 'Not provided'}
        </span>
      </div>
      <div className="flex gap-x-1">
        <span className="w-7 shrink-0 text-mid-grey">Respond by</span>
        <span className="text-darkest-grey">
          {evidenceDueBy
            ? datetimeFormat({
                dateString: evidenceDueBy,
                pattern: DATE_TIME_FORMAT.MMM_DD_YYYY,
                timezone,
              })
            : 'Not provided'}
        </span>
      </div>
      <span className="mt-0.4 text-mid-grey">
        Respond to this dispute in your Stripe dashboard.
      </span>
    </div>
  );
};

export { DisputeDetailTooltip };
export default DisputeDetail;
